/**
 * Needs you (decision 0056): what waits on a person, grouped by the verb it asks for, and the one count of it. The
 * Needs-you tab's rows and the count everywhere in a project (header, phone tab bar, Overview, Team, the chat aside)
 * come from the same predicates here, so the number always equals the rows.
 */
import { authorize, mayDecide } from "./authorize"
import { cardItemIds } from "./card-chain"
import { myWork, myWorkBadge } from "./derived"
import { lendRequestsFor, type RunnerFactsOf, recipientOf, stuckRecipientOf } from "./multiplayer"
import { reviewIntentKey } from "./runs"
import { isOpenItem, isOpenProposal, isRunEnded, itemOf, memberOf, runOf } from "./state"
import { childrenOf, topItemOf } from "./tree"
import type { Actor, Item, ItemId, MyWorkRow, ProjectState, Proposal } from "./types"

/** The six groups of the Needs-you tab, in their fixed order. */
export type NeedsYouVerb = "decide" | "answer" | "review" | "allow" | "do" | "sort"

/** The Needs-you groups in their fixed order (Decide · Answer · Review · Allow · Do · Sort). */
export const NEEDS_YOU_VERBS: ReadonlyArray<NeedsYouVerb> = [
  "decide",
  "answer",
  "review",
  "allow",
  "do",
  "sort",
]

/** How many rows of each group ask something of the viewer, and their sum: the one Needs-you count. */
export interface NeedsYouTally {
  readonly decide: number
  readonly answer: number
  readonly review: number
  readonly allow: number
  readonly do: number
  readonly sort: number
  readonly total: number
}

/**
 * The verb of an open card: a human review (its intent key is `review:<item>:<round>`, `runs.ts`) → review; a question
 * or an escalation → answer; a permission prompt or an outward action → allow; a plan change, a memory proposal, an
 * agent's monitor (wave C, decision 0071: Remember / Accept · Edit first · Not this) and any kind this function does
 * not know yet → decide.
 */
export const verbOfProposal = (s: ProjectState, p: Proposal): Exclude<NeedsYouVerb, "do" | "sort"> => {
  const body = p.body as {
    readonly _tag: string
    readonly subject?: { readonly _tag: string; readonly id: string } | null
  }
  switch (body._tag) {
    case "question": {
      const subject = body.subject
      if (subject?._tag === "item") {
        const item = s.items.find((t) => t.id === subject.id)
        if (item && p.intentKey === reviewIntentKey(item.id, item.review.round)) return "review"
      }
      return "answer"
    }
    case "permission":
    case "outward_action":
      return "allow"
    case "memory":
    case "monitor":
      return "decide"
    default:
      return "decide"
  }
}

/**
 * An escalation about an item that is closed since (done or cancelled): Retry would act on a closed item, so the card
 * asks nobody anything. It stays open in the log; Needs you and the count leave it out.
 */
export const isStaleEscalation = (s: ProjectState, p: Proposal): boolean => {
  if (p.body._tag !== "question" || !p.body.escalation) return false
  const subject = p.body.subject
  if (subject?._tag !== "item") return false
  const item = s.items.find((t) => t.id === subject.id)
  return item !== undefined && (item.status === "done" || item.status === "cancelled")
}

/**
 * A card that asks the viewer (For you, decision 0063): an open proposal they may decide (core `mayDecide`), any tier,
 * except an escalation about a closed item; for a person, only when they are its recipient (`recipientOf`): anyone may
 * decide any card, but each card is for one person. The web's card list (`model/needs-you.ts` `needsYou`) uses this
 * predicate, so the count equals the cards shown. `runners` tells a personal machine from a team one (permission
 * prompts); without it the run's principal stands in for the machine's owner.
 */
export const isCardFor = (s: ProjectState, viewer: Actor, p: Proposal, runners?: RunnerFactsOf): boolean =>
  p.state === "open" &&
  isOpenProposal(p) &&
  !isStaleEscalation(s, p) &&
  mayDecide(s, viewer, p) === null &&
  // decision 0102: a card an agent holds on its way up the chain is not (yet) the person's
  (viewer._tag !== "user" || (p.approver._tag !== "agent" && recipientOf(s, p, runners) === viewer.userId))

/**
 * May this viewer sort triage? A person who is not a project `viewer` (the web's `edit_plan` rule,
 * `features/settings/permissions.ts`); never an agent.
 */
export const maySort = (s: ProjectState, viewer: Actor): boolean =>
  viewer._tag === "user" && memberOf(s, viewer.userId)?.role !== "viewer"

/** Is an open escalation card about this item (it blocks it, or the item is its subject)? */
const hasEscalation = (s: ProjectState, itemId: string): boolean =>
  s.proposals.some(
    (p) =>
      isOpenProposal(p) &&
      p.body._tag === "question" &&
      p.body.escalation !== null &&
      (p.body.subject?.id === itemId || p.blocks.some((b) => b._tag === "item" && b.id === itemId)),
  )

/**
 * An item blocked with nobody's card open, that this viewer may retry (sim fixes S04):
 * - `blocked{reported}` whose run ended with a stop other than `completed` and whose accepted outcome is not the
 *   worker's own `blocked` report (a platform failure, not the worker's blocker, which the owner already has);
 * - `blocked{needs_human}` whose card is no longer open;
 * and in both cases only when the item is not paused (a person paused it: that person decided) and no open escalation
 * card names the item (the card is the row then). Never for an agent or someone who may not retry it.
 */
export const isStuckFor = (s: ProjectState, viewer: Actor, item: Item): boolean =>
  viewer._tag === "user" &&
  isStuckForAnyone(s, viewer, item) &&
  // wave E (decision 0063): each stuck item is for one person (its asker, else the keeper); others see it under Others
  stuckRecipientOf(s, item) === viewer.userId

/**
 * Stuck as `isStuckFor` says, whoever it is for: the viewer may retry it (Others shows these, "for Becky", when the
 * viewer isn't the recipient).
 */
export const isStuckForAnyone = (s: ProjectState, viewer: Actor, item: Item): boolean => {
  if (viewer._tag !== "user") return false
  if (item.status !== "blocked" || !item.blocked || item.paused) return false
  const b = item.blocked
  if (b._tag === "reported" && b.runId) {
    const run = s.runs.find((r) => r.id === b.runId)
    if (run?.state._tag !== "ended" || run.state.stopReason === "completed") return false
    if (run.outcome?._tag === "blocked") return false
  } else if (b._tag === "needs_human") {
    const card = s.proposals.find((p) => p.id === b.proposalId)
    if (card && isOpenProposal(card)) return false
  } else return false
  if (hasEscalation(s, item.id)) return false
  return authorize(s, viewer, { _tag: "RetryItem", itemId: item.id }) === null
}

/**
 * A My work row that asks the viewer for an answer: their own `question` item, ready for them. It is counted (and
 * listed) under Answer instead of Do; the total stays the same.
 */
export const isAnswerRow = (r: Pick<MyWorkRow, "type" | "section">): boolean =>
  (r.type as string) === "question" && (r.section === "ready" || r.section === "ready_to_close")

/**
 * The tally of rows already derived: the one place that adds them up. `cards` are the open cards the viewer may decide
 * (each counted under its verb), `stuck` the stuck items (Answer), `mine` the viewer's My work rows (only Ready for
 * you and Ready to close count, their own questions under Answer: in progress, waiting on you and later are folded as "Also yours"), `sort` the items to
 * sort (0 when the viewer may not sort). `s` names the verb of each card (a human review needs its item's round).
 */
export const tallyOf = (
  s: ProjectState,
  rows: {
    readonly cards: ReadonlyArray<Proposal>
    readonly stuck: number
    readonly mine: ReadonlyArray<MyWorkRow>
    readonly sort: number
    /** Wave E (decision 0063): the viewer's lend rows ("Run DIC-155 on jannik-mbp?"), counted in Do. */
    readonly lend?: number
  },
): NeedsYouTally => {
  // The viewer's own open questions (type `question`, ready for them) ask for an answer: Answer, not Do.
  const questions = rows.mine.filter(isAnswerRow).length
  const t = { decide: 0, answer: rows.stuck + questions, review: 0, allow: 0 }
  for (const p of rows.cards) t[verbOfProposal(s, p)] += 1
  const doCount = myWorkBadge(rows.mine) - questions + (rows.lend ?? 0)
  return {
    ...t,
    do: doCount,
    sort: rows.sort,
    total: t.decide + t.answer + t.review + t.allow + doCount + rows.sort,
  }
}

/** Items in triage (the Sort group's rows), counted only for a viewer who may sort. */
export const sortItemsFor = (s: ProjectState, viewer: Actor): ReadonlyArray<Item> =>
  maySort(s, viewer) ? s.items.filter((t) => t.status === "triage") : []

/**
 * Why an item (not a card) waits on the viewer (decision 0163): `do` their item is ready for them, `close` their
 * container's work is done and waits for them to close it, `answer` their own question item, `stuck` an item stuck
 * with nobody's card that is theirs to retry, `lend` a run that could use their machine, `sort` an item in triage.
 */
export type NeedsYouItemWhy = "do" | "close" | "answer" | "stuck" | "lend" | "sort"

/** One item whose next move is the viewer's, as Needs you lists it next to the cards (decision 0163). */
export interface NeedsYouItem {
  readonly itemId: ItemId
  readonly ref: string
  readonly title: string
  /** The Needs-you group it is counted under. */
  readonly verb: Extract<NeedsYouVerb, "answer" | "do" | "sort">
  readonly why: NeedsYouItemWhy
  /**
   * When it became the viewer's move, as well as the state knows it: its last spec change, the last of its blockers to
   * close, its children's last close (Ready to close), the run that got stuck or waits. Its age is `now - since`.
   */
  readonly since: number
  /** How many open items wait on it (My work's `holds`; 0 for the other reasons). */
  readonly holds: number
}

/**
 * Everything that waits on the viewer in one project (decision 0163): the cards (`isCardFor`) and the items whose move
 * is theirs. `tally` counts exactly these rows (`tally.total === cards.length + items.length`), so every surface that
 * lists or counts Needs you (the web's tab and Home, `needs_me`, `read_project.needsMe`, `list_projects.needsMe`)
 * agrees. A person sees their own items and what is asked of them; an agent gets cards only.
 */
export interface NeedsYouList {
  readonly cards: ReadonlyArray<Proposal>
  readonly items: ReadonlyArray<NeedsYouItem>
  readonly tally: NeedsYouTally
}

const latest = (...ats: ReadonlyArray<number | null | undefined>): number =>
  ats.reduce<number>((m, a) => (typeof a === "number" && a > m ? a : m), 0)

/** When an open item became its person's move: its last spec change, its blockers' last close, a passed not-before. */
const moveSince = (s: ProjectState, t: Item, now: number): number =>
  latest(
    t.specRev.at,
    t.notBefore !== null && t.notBefore <= now ? t.notBefore : null,
    ...s.edges
      .filter((e) => e.kind === "blocks" && e.to.id === t.id)
      .map((e) => itemOf(s, e.from.id as ItemId)?.closedAt),
  )

/**
 * Decision 0163: since when a My work row ready for its person (Ready for you, Ready to close) has waited on them; the
 * Needs-you tab's Do rows and `needsYouOf` show the same age. 0 when the item is unknown.
 */
export const myWorkSince = (
  s: ProjectState,
  r: Pick<MyWorkRow, "itemId" | "section">,
  now: number,
): number => {
  const t = itemOf(s, r.itemId)
  if (!t) return 0
  const since =
    r.section === "ready_to_close"
      ? latest(...childrenOf(s, t.id).map((c) => c.closedAt))
      : moveSince(s, t, now)
  return since > 0 ? since : t.specRev.at
}

/** When a stuck item got stuck: its run's end, else the close of the card that blocked it, else its last spec change. */
const stuckSince = (s: ProjectState, t: Item): number => {
  const b = t.blocked
  if (b?._tag === "reported" && b.runId) return latest(runOf(s, b.runId)?.endedAt, t.specRev.at)
  if (b?._tag === "needs_human")
    return latest(s.proposals.find((p) => p.id === b.proposalId)?.resolution?.at, t.specRev.at)
  return t.specRev.at
}

/** Needs you for one viewer in one project: cards, items whose move is theirs, and the one count of both. Pure. */
export const needsYouOf = (
  s: ProjectState,
  viewer: Actor,
  now: number,
  runners?: RunnerFactsOf,
): NeedsYouList => {
  const cards = s.proposals.filter((p) => isCardFor(s, viewer, p, runners))
  const stuck = s.items.filter((t) => isStuckFor(s, viewer, t))
  const mine = viewer._tag === "user" ? myWork(s, viewer.userId, now) : []
  const sort = sortItemsFor(s, viewer)
  const lend = viewer._tag === "user" ? lendRequestsFor(s, viewer.userId) : []
  const tally = tallyOf(s, { cards, stuck: stuck.length, mine, sort: sort.length, lend: lend.length })
  const row = (
    t: Item,
    verb: NeedsYouItem["verb"],
    why: NeedsYouItemWhy,
    since: number,
    holds = 0,
  ): NeedsYouItem => ({
    itemId: t.id,
    ref: t.ref,
    title: t.title,
    verb,
    why,
    since: since > 0 ? since : t.specRev.at,
    holds,
  })
  const items: Array<NeedsYouItem> = []
  for (const t of stuck) items.push(row(t, "answer", "stuck", stuckSince(s, t)))
  // My work's order (Ready for you, then Ready to close; overdue, due date, rank); only these two sections count
  for (const r of mine) {
    if (r.section !== "ready" && r.section !== "ready_to_close") continue
    const t = itemOf(s, r.itemId)
    if (!t) continue
    const why: NeedsYouItemWhy = isAnswerRow(r) ? "answer" : r.section === "ready_to_close" ? "close" : "do"
    items.push(row(t, why === "answer" ? "answer" : "do", why, myWorkSince(s, r, now), r.holds))
  }
  for (const l of lend) {
    const t = itemOf(s, l.itemId)
    const run = runOf(s, l.runId)
    if (t) items.push(row(t, "do", "lend", run?.state._tag === "waiting" ? run.state.since : 0))
  }
  for (const t of sort) items.push(row(t, "sort", "sort", t.specRev.at))
  return { cards, items, tally }
}

/**
 * The one Needs-you count: the rows of the Needs-you tab that ask something of the viewer, by verb. Cards of every
 * tier, stuck items, the viewer's items ready to start or close, their lend rows (Do), and items to sort. Agents get
 * cards only. The rows are `needsYouOf`'s.
 */
export const needsYouTally = (
  s: ProjectState,
  viewer: Actor,
  now: number,
  runners?: RunnerFactsOf,
): NeedsYouTally => needsYouOf(s, viewer, now, runners).tally

/** How long something has waited, in plain words: "under an hour", "5 h", "3 days". */
export const waitedWords = (ms: number): string => {
  const h = Math.floor(ms / 3_600_000)
  if (h < 1) return "under an hour"
  if (h < 48) return `${h} h`
  return `${Math.floor(h / 24)} days`
}

/**
 * Decision 0134 (lesson 3, 0133 §5): how much an open card holds up, for ranking what waits on a person (Needs you, the
 * brief). Higher first: live runs it holds, then the open work that waits on it (its items and everything that waits
 * on them by `blocks`, transitively), then whether that work is under a top-level item in Now. A number, so it travels
 * with a card across projects. Pure.
 */
export const askRank = (s: ProjectState, p: Proposal): number => {
  const start = new Set<string>(cardItemIds(p))
  const b = p.body
  if (b._tag === "question" && b.escalation?.runId) {
    const r = runOf(s, b.escalation.runId)
    if (r?.itemId) start.add(r.itemId)
  }
  const held = new Set<string>()
  const queue = [...start]
  while (queue.length > 0) {
    const id = queue.shift() as string
    if (held.has(id)) continue
    const t = itemOf(s, id as never)
    if (!t || !isOpenItem(t)) continue
    held.add(id)
    for (const e of s.edges) if (e.kind === "blocks" && e.from.id === id) queue.push(e.to.id)
  }
  const liveRunIds = new Set<string>()
  for (const x of p.blocks)
    if (x._tag === "run") {
      const r = runOf(s, x.id as never)
      if (r && !isRunEnded(r)) liveRunIds.add(r.id)
    }
  for (const r of s.runs) if (r.itemId && held.has(r.itemId) && !isRunEnded(r)) liveRunIds.add(r.id)
  const live = liveRunIds.size + (b._tag === "question" ? (b.escalation?.held?.length ?? 0) : 0)
  const inNow = [...held].some((id) => {
    const t = itemOf(s, id as never)
    return t !== undefined && topItemOf(s, t).status === "in_progress"
  })
  return live * 1_000_000 + Math.min(held.size, 999) * 1_000 + (inNow ? 1 : 0)
}
